import React, { useEffect, useMemo, useState } from 'react';
import {
  getMainPageSlice,
  setMainPagePeriod
} from '../../store/features/filters/MainPageSlice/MainPageSlice';
import { Box, ChevronRightIcon, IconButton } from '@esgian/esgianui';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { useQuery } from '@tanstack/react-query';
import { fetchRigWells } from '../../api/DdrReport';
import TimeRangeSlider from './TimeRangeSlider';
import WellsButton from './WellsButton';
import BarChart from './BarChart';
import DateRangeDropdown from '../DateRangeDropdown';
import { useTimezone } from '@hooks/useTimezone';
import { DATE_FORMAT } from '@constants/dates';

export const DateSlider = () => {
  const dispatch = useDispatch();
  const {
    selectedRig,
    startDate: selectedStartDate,
    endDate: selectedEndDate,
    selectedDateType
  } = useSelector(getMainPageSlice);
  const { selectedTimeZone } = useTimezone();
  const { selectedDateName } = useSelector(getMainPageSlice);

  const [currentSelectedDates, setCurrentSelectedDates] = useState<[string, string]>([
    selectedStartDate,
    selectedEndDate
  ]);

  useEffect(() => {
    setCurrentSelectedDates([selectedStartDate, selectedEndDate]);
  }, [selectedStartDate, selectedEndDate]);

  const fullStartDate = useMemo(() => {
    // lowerLimit is a yyyy-MM-dd date, so it is read as midnight in the displayed zone. Parsing it first and
    // converting afterwards lands on the previous day whenever that zone is behind the browser's.
    return moment.tz(selectedRig.lowerLimit, DATE_FORMAT, selectedTimeZone);
  }, [selectedTimeZone, selectedRig]);

  const fullEndDate = useMemo(() => {
    return moment().tz(selectedTimeZone);
  }, [selectedTimeZone, selectedRig]);

  const isMoreThanOneYear = useMemo(
    () => fullEndDate.diff(fullStartDate, 'months') > 12,
    [fullStartDate, fullEndDate]
  );

  const initialDisplayedStart = useMemo(() => {
    return isMoreThanOneYear ? fullEndDate.clone().subtract(12, "months") : fullStartDate.clone();
  }, [isMoreThanOneYear]);

  const initialDisplayedEnd = useMemo(() => fullEndDate, [fullEndDate]);

  const [currentDisplayedDates, setCurrentDisplayedDates] = useState([
    initialDisplayedStart,
    initialDisplayedEnd
  ]);

  const handleMoveTimeline = (direction: string) => {
    const [currentStart, currentEnd] = currentDisplayedDates;
    const newStart =
      direction === 'left' ? currentStart.subtract(30, "days") : currentStart.add(30, "days");

    const newEnd =
      direction === 'left' ? currentEnd.subtract(30, "days") : currentEnd.add(30, "days");

    if (newStart < fullStartDate) {
      setCurrentDisplayedDates([fullStartDate, currentDisplayedDates[1]]);
    } else if (newEnd > fullEndDate) {
      setCurrentDisplayedDates([currentDisplayedDates[0], fullEndDate]);
    } else {
      setCurrentDisplayedDates([newStart, newEnd]);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const [startDate, endDate] = currentSelectedDates;
      const isStartDateChanged =
        !moment(startDate).tz(selectedTimeZone).isSame(moment(selectedStartDate).tz(selectedTimeZone));
      const isEndDateChanged =
        !moment(endDate).tz(selectedTimeZone).isSame(moment(selectedEndDate).tz(selectedTimeZone));
      if (isStartDateChanged || isEndDateChanged) {
        onSave({ startDate, endDate });
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [currentSelectedDates]);

  const onSave = (period: { startDate: string; endDate: string }, type?: string, name?: string) => {
    dispatch(setMainPagePeriod({ ...period, type, name }));
  };

  const wellsLookup = useQuery({
    queryKey: ['lookupWells', { selectedRig: selectedRig }],
    enabled: !!selectedRig,
    placeholderData: [],
    queryFn: ({ queryKey, signal }: any) => {
      return fetchRigWells(signal, { ...queryKey[1] })
        .then((result: any) => {
          return result.filter(({ starttime, endtime }: { starttime: string; endtime: string }) => {
            return (
              moment(starttime) >= fullStartDate &&
              moment(endtime) <= fullEndDate
            );
          });
        })
        .catch(() => []);
    }
  });

  const handleCurrenDisplayedDateChange = (range: [string, string]) => {
    setCurrentDisplayedDates([moment(range[0]), moment(range[1])]);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }} id={'date-range-section'}>
      <DateRangeDropdown
        startDate={currentSelectedDates[0]}
        endDate={currentSelectedDates[1]}
        selectedDateName={selectedDateName || ''}
        onSave={(period) => dispatch(setMainPagePeriod(period))}
        inputVariant="noBorder"
        maxWidth={310}
        fullWidth={true}
        allowTimeZoneChange
        selectedRig={selectedRig}
        selectedDateType={selectedDateType}
      />
      <Box sx={{ display: 'flex', mt: '8px' }}>
        {isMoreThanOneYear ? (
          <IconButton
            size={'small'}
            onClick={() => handleMoveTimeline('left')}
            sx={{ padding: 0, marginRight: 1, rotate: '180deg', height: 56 }}
            disabled={currentDisplayedDates[0].isSameOrBefore(fullStartDate)}>
            <ChevronRightIcon fontSize="small" color={'inherit'} />
          </IconButton>
        ) : null}
        <Box
          sx={{
            width: '100%',
            height: '72px',
            position: 'relative',
            touchAction: 'none',
            userSelect: 'none'
          }}
          id={'date-range-slider'}>
          <WellsButton
            wells={wellsLookup.data}
            startDate={currentDisplayedDates[0].format(DATE_FORMAT)}
            endDate={currentDisplayedDates[1].format(DATE_FORMAT)}
            selectedRange={currentSelectedDates}
            onWellClick={onSave}
          />
          <BarChart
            selectedRig={selectedRig}
            minStartDate={
              fullStartDate.clone()
                .startOf('day')
                .format(DATE_FORMAT)
            }
            maxEndDate={
              fullEndDate.clone()
                .endOf('day')
                .format(DATE_FORMAT)
            }
            startDate={currentDisplayedDates[0].format(DATE_FORMAT)}
            endDate={currentDisplayedDates[1].format(DATE_FORMAT)}
            setCurrentDisplayedDates={handleCurrenDisplayedDateChange}
            selectedRange={currentSelectedDates}
          />
          <TimeRangeSlider
            startDate={currentDisplayedDates[0].format(DATE_FORMAT)}
            endDate={currentDisplayedDates[1].format(DATE_FORMAT)}
            selectedRange={currentSelectedDates}
            onRangeChange={setCurrentSelectedDates}
          />
        </Box>
        {isMoreThanOneYear ? (
          <IconButton
            size={'small'}
            onClick={() => handleMoveTimeline('right')}
            sx={{ padding: 0, marginLeft: 1, height: 56 }}
            disabled={currentDisplayedDates[1].isSameOrAfter(fullEndDate)}>
            <ChevronRightIcon fontSize="small" color={'inherit'} />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
};
